import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChefHat, Users, Layers, Gauge, ArrowUpRight, Zap } from "lucide-react";

const kitchenBenefits = [
    {
        icon: Users,
        title: "Embedded Engineers",
        desc: "Plug-in licensed PEs, designers and BIM specialists who work as an extension of your team — under your brand, under your QA/QC.",
    },
    {
        icon: Layers,
        title: "Overflow Capacity",
        desc: "Hit a pipeline crunch? We absorb overflow in days, not months — so you never turn work away or blow past deadlines.",
    },
    {
        icon: Gauge,
        title: "Fractional Cost Model",
        desc: "All the senior talent of a full team, billed as needed. No benefits, no benching, no long-term commitment.",
    },
];

export default function StaffAugmentation() {
    return (
        <section className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden" data-testid="staff-augmentation">
            <div className="absolute inset-0 bg-grid bg-grid-fade opacity-25 pointer-events-none" />
            <div className="absolute -top-20 -right-40 w-[600px] h-[600px] rounded-full bg-cyan-500/10 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 -left-40 w-[500px] h-[500px] rounded-full bg-blue-600/15 blur-[120px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                {/* Hero banner card */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="relative overflow-hidden rounded-3xl border border-cyan-500/30 bg-gradient-to-br from-blue-700 via-blue-800 to-slate-900 shadow-[0_30px_100px_rgba(37,99,235,0.35)]"
                    data-testid="staff-aug-banner"
                >
                    <div className="absolute inset-0 bg-grid opacity-15" />
                    <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-cyan-400/25 blur-[100px]" />
                    <div className="absolute top-10 right-10 opacity-20">
                        <ChefHat className="w-48 h-48 text-cyan-300" strokeWidth={1} />
                    </div>

                    <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 p-8 md:p-16">
                        <div className="lg:col-span-8">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-400/20 border border-cyan-300/40 backdrop-blur-md mb-4">
                                <Zap className="w-3.5 h-3.5 text-cyan-200" />
                                <span className="text-xs font-mono tracking-[0.2em] uppercase text-cyan-100">
                                    The Engineering Production Kitchen
                                </span>
                            </div>
                            <div className="text-sm md:text-base font-medium text-cyan-100/80 tracking-wide mb-8">
                                Professional Engineering Solutions <span className="text-white font-semibold">(PES)</span>
                            </div>

                            <h2 className="font-heading font-black text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] tracking-tighter">
                                Your back-of-house.<br />
                                <span className="bg-gradient-to-r from-cyan-200 via-cyan-300 to-white bg-clip-text text-transparent">
                                    Our production line.
                                </span>
                            </h2>

                            <p className="mt-8 text-lg md:text-xl text-blue-100/90 leading-relaxed max-w-2xl">
                                PES operates as a dedicated <span className="font-semibold text-white">engineering production hub</span> — the
                                "kitchen" behind large, busy engineering firms. When you're drowning in pipeline, need overflow capacity,
                                or want to scale a team without scaling headcount — we plug in seamlessly, run quietly, and ship work under your brand.
                            </p>

                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <Link
                                    to="/contact"
                                    className="group inline-flex items-center gap-3 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold px-7 py-4 rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(0,229,255,0.5)] hover:shadow-[0_0_50px_rgba(0,229,255,0.8)]"
                                    data-testid="staff-aug-cta"
                                >
                                    Request Staff Augmentation
                                    <ArrowUpRight className="w-5 h-5 transition-transform group-hover:rotate-45" />
                                </Link>
                                <Link
                                    to="/services"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-200 hover:text-white transition-colors group"
                                >
                                    Learn how it works
                                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-4 flex flex-col gap-3">
                            {[
                                { t: "48h", l: "Onboarding Time" },
                                { t: "30-40%", l: "Cost Savings vs In-House" },
                                { t: "100%", l: "Under Your Brand / QA" },
                            ].map((x) => (
                                <div
                                    key={x.l}
                                    className="rounded-2xl bg-slate-900/40 backdrop-blur-md border border-white/15 p-5"
                                >
                                    <div className="font-heading font-black text-white text-3xl md:text-4xl leading-none">{x.t}</div>
                                    <div className="mt-2 text-xs tracking-[0.15em] uppercase text-cyan-200">{x.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Benefits row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    {kitchenBenefits.map((b, i) => {
                        const Icon = b.icon;
                        return (
                            <motion.div
                                key={b.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="relative p-8 rounded-2xl bg-slate-900/40 backdrop-blur-xl border border-white/5 hover:border-cyan-500/40 transition-all duration-500 group overflow-hidden"
                                data-testid={`kitchen-benefit-${i}`}
                            >
                                <div className="absolute -bottom-16 -right-16 w-40 h-40 rounded-full bg-cyan-500/5 blur-2xl group-hover:bg-cyan-500/15 transition-colors duration-500" />
                                <div className="relative">
                                    <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-5">
                                        <Icon className="w-5 h-5 text-cyan-300" />
                                    </div>
                                    <h3 className="font-heading font-bold text-white text-xl mb-3">{b.title}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
